import { Module } from 'vuex'
import { IRootState } from '@/store/types'
import {
    TransactionResponse,
    TransactionQueryResponse,
    TransactionsState,
    TransactionQuery,
    EVMTransactionQueryResponse,
} from './models'
import {
    EVMBlockQueryResponse,
    RawEVMBlockResponse,
} from '@/store/modules/blocks/models'
import { getTransaction, ITransactionPayload } from '@/services/transactions'
import {
    getEVMTransaction,
    getEVMTransactionByHash,
    IEVMTransactionParams,
} from '@/services/evmtransactions'
import { Transaction } from '@/js/Transaction'
import { parseTxs } from './helpers'
import { getEVMBlock } from '@/services/evmblocks'
import { parseEVMBlocks } from '@/store/modules/blocks/helpers'
import { parseEVMTxs, ParsedEVMTx } from './helpers/parseEVMTxs'
import { CURRENT_SUBNET } from '../network/network'

const defaultState: TransactionsState = {
    tx: null,
    txRes: {
        startTime: '',
        endTime: '',
        next: '',
        transactions: [],
    },
    recentTxRes: {
        startTime: '',
        endTime: '',
        next: '',
        transactions: [],
    },
    assetTxRes: {
        startTime: '',
        endTime: '',
        next: '',
        transactions: [],
    },
    addressTxRes: {
        startTime: '',
        endTime: '',
        next: '',
        transactions: [],
    },
    blockchainTxRes: {
        startTime: '',
        endTime: '',
        next: '',
        transactions: [],
    },
    evmTx: null,
    evmTxs: [],
    fetching: false,
}

const transactions_module: Module<TransactionsState, IRootState> = {
    namespaced: true,
    modules: {},
    state: defaultState,
    mutations: {
        addTx(state, tx: Transaction) {
            state.tx = tx
        },
        addTxs(state, txRes: TransactionQuery) {
            state.txRes = txRes
        },
        addRecentTxs(state, txRes: TransactionQuery) {
            state.recentTxRes = txRes
        },
        addAssetTxs(state, txRes: TransactionQuery) {
            state.assetTxRes = txRes
        },
        addAddressTxs(state, txRes: TransactionQuery) {
            state.addressTxRes = txRes
        },
        addBlockchainTxs(state, txRes: TransactionQuery) {
            state.blockchainTxRes = txRes
        },
        addEVMTx(state, evmTx: ParsedEVMTx) {
            state.evmTx = evmTx
        },
        addEVMTxs(state, evmTxs: ParsedEVMTx[]) {
            state.evmTxs = evmTxs
        },
        fetching(state, value) {
            state.fetching = value
        },
    },
    actions: {
        async getTx(store, payload: ITransactionPayload) {
            const txRes: TransactionResponse = await getTransaction(payload.id)
            if (txRes) store.commit('addTx', new Transaction(txRes))
        },
        async getTxs(store, payload: ITransactionPayload) {
            const txRes: TransactionQueryResponse = await getTransaction(
                payload.id,
                payload.params
            )
            store.commit('addTxs', parseTxs(txRes))
        },
        async getRecentTxs(store, payload: ITransactionPayload) {
            const txRes: TransactionQueryResponse = await getTransaction(
                payload.id,
                payload.params
            )
            store.commit('addRecentTxs', parseTxs(txRes))
        },
        async getTxsByAddress(store, payload: ITransactionPayload) {
            const txRes: TransactionQueryResponse = await getTransaction(
                payload.id,
                payload.params
            )
            store.commit('addAddressTxs', parseTxs(txRes))
        },
        async getTxsByAsset(store, payload: ITransactionPayload) {
            const txRes: TransactionQueryResponse = await getTransaction(
                payload.id,
                payload.params
            )
            store.commit('addAssetTxs', parseTxs(txRes))
        },
        async getTxsByBlockchain(store, payload: ITransactionPayload) {
            const txRes: TransactionQueryResponse = await getTransaction(
                payload.id,
                payload.params
            )
            store.commit('addBlockchainTxs', parseTxs(txRes))
        },
        async getNFTPayloads(store, payload: ITransactionPayload) {
            // get the asset creation tx
            const txRes: TransactionResponse = await getTransaction(payload.id)
            // find the NFT Minting Right UTXO
            const NFTMintUTXO = txRes.outputs.find(
                (utxo) => utxo.outputType === 10
            )
            // the redeemedTx of the NFT Minting Right UTXO has the payloads
            const txResNFT: TransactionResponse = await getTransaction(
                NFTMintUTXO?.redeemingTransactionID
            )
            // get a list of payload tuples [payload, groupID]
            // remove empty strings and duplicates
            const payloads = txResNFT.outputs
                .map((utxo) => [utxo.payload, utxo.groupID])
                .filter((payload: (string | number | null)[]) => {
                    if (payload[0]) return (payload[0] as string).length !== 0
                })
                .filter((value, index, self) => self.indexOf(value) === index)
            return payloads
        },
        async getEVMTx(store, params: IEVMTransactionParams) {
            store.commit('fetching', true)
            const txRes: EVMTransactionQueryResponse = await getEVMTransaction(
                params
            )
            if (txRes['Transactions'].length == 0) {
                store.commit('fetching', false)
                return
            }
            let blockRes: EVMBlockQueryResponse
            const tx = txRes.Transactions[0]
            if (tx) {
                await getEVMBlock({
                    height: tx.block,
                })
                this.state.Blocks.socket.addEventListener(
                    'message',
                    function (event) {
                        const data: any = { data: JSON.parse(event.data) }
                        const blockData = data
                        const rawBlockData: RawEVMBlockResponse =
                            blockData.data.result

                        const parsedBlock: EVMBlockQueryResponse = {
                            ...rawBlockData,
                            blockNumber: parseInt(
                                rawBlockData.number,
                                16
                            ).toString(),
                            header: {
                                ...rawBlockData,
                            },
                        }
                        blockRes = parseEVMBlocks(parsedBlock)
                        store.dispatch('Blocks/addEVMBlock', blockRes, {
                            root: true,
                        })
                        const parsedTx: ParsedEVMTx = parseEVMTxs(tx, blockRes)
                        const fetchAddr = async () => {
                            const txHash: string = parsedTx.hash || ''
                            const data = await getEVMTransactionByHash({
                                hash: txHash,
                            })
                            parsedTx.fromAddr = data.from
                        }
                        fetchAddr()
                        store.commit('addEVMTx', parsedTx)
                        store.commit('fetching', false)
                    }
                )
            }
        },
        async getEVMTxs(store, payload: { params: IEVMTransactionParams }) {
            if (
                // window.location.href.includes('blockchain') ||
                // localStorage.getItem('isTSP')
                CURRENT_SUBNET !== null
            ) {
                const block = this.state.Blocks.evmBlocks
                const transactions: any[] = []
                block.map((x: any) => {
                    const txCount = x.transactions.length
                    let i = 0
                    while (i < txCount) {
                        transactions.push(x.transactions[i])
                        i = i + 1
                    }
                })
                store.commit('addEVMTxs', transactions)
            } else {
                const txRes: EVMTransactionQueryResponse = await getEVMTransaction(
                    payload.params
                )
                store.commit('addEVMTxs', txRes.Transactions)
            }
        },
    },
}
export default transactions_module
